import { useMutation } from "@tanstack/react-query";
import { Alert, Form, Modal, Select } from "antd";
import { Rule } from "antd/lib/form";
import { useForm } from "antd/lib/form/Form";
import axios from "axios";
import useAvailablePackageTypes from "hooks/useAvailablePackageTypes";
import { useAuthenticatedRequestCreator } from "hooks/useRequests";
import { Owners } from "models/applicationUsers";
import { PackageTypeOption } from "models/common";
import { PackageType, StaffPackage } from "models/packages";
import { useEffect, useState } from "react";
import { mapOwners, ownershipUpdate, ownershipValidator } from "util/ownership";
import OwnershipSelector from "../OwnershipSelector";

interface CloneForm {
    packageTypeId: string;
    owners: Owners
}

const usePackageCloning = () => {
    const clone = useAuthenticatedRequestCreator<StaffPackage, { packageId: string, packageTypeId: string, owners: Owners }>(
        ({ packageId, packageTypeId, owners }) => ({
            method: "post",
            url: "/packages/clone",
            data: {
                id: packageId,
                packageTypeId,
                customerIds: owners.customers.map(c => c!.id),
                accountIds: owners.silentUsers.map(u => u!.accountId)
            }
        }), [400, 404, 409]
    );

    const { mutateAsync: clonePackage, isLoading } = useMutation(clone);

    return { clonePackage, isLoading };
};

const ClonePackageModal = (props: {
    open: boolean;
    package: StaffPackage;
    onClose: (cloned?: boolean) => void;
}) => {
    const { data: types } = useAvailablePackageTypes(false, props.package.packageTypeId);
    const [currentPkgType, setCurrentPkgType] = useState<PackageType>();
    const [error, setError] = useState<string>();
    const [clone, setClone] = useState<StaffPackage>();
    const [form] = useForm<CloneForm>();
    const { clonePackage, isLoading } = usePackageCloning();

    useEffect(() => {
        if (types) {
            setCurrentPkgType(types.find(t => t.id === props.package.packageTypeId));
        }
    }, [props.package.packageTypeId, types]);

    const togglePackageTypeOptions = (_: string, option: PackageTypeOption | PackageTypeOption[]) => {
        const opt = option as PackageTypeOption;
        // if the current and selected package types have mismatched support for silent accounts,
        // clear ownership to force the user to re-select who owns the package
        const owners: Owners =
            currentPkgType!.supportSilentAccounts === opt.packageType.supportSilentAccounts
                ? form.getFieldValue('owners')
                : { customers: [], silentUsers: [] }

        setCurrentPkgType(opt.packageType);
        form.setFieldsValue({ owners });
    };

    const closeAndResetModal = () => {
        props.onClose(!!clone);

        form.resetFields();
    };

    const submitForm = async () => {
        try {
            setError(undefined);
            const validForm = await form.validateFields();

            const result = await clonePackage({
                packageId: props.package.id,
                packageTypeId: currentPkgType!.id,
                owners: ownershipUpdate(
                    mapOwners(props.package),
                    validForm.owners
                )
            });

            setClone(result);
        } catch (error) {
            if (axios.isAxiosError(error)) {
                determineError(error.response?.status ?? 0);
            }
        }
    };

    const determineError = (code: number) => {
        switch (code) {
            case 400:
                setError("The package type and/or ownership could not be resolved.");
                break;
            case 404:
                setError("The source package does not exist!");
                break;
            case 409:
                setError("This package has already been cloned and cannot be cloned again.");
                break;

            default:
                setError("An unknown error occurred when attempting to clone the package");
                break;
        }
    }

    return <Modal
        title={`Clone Package #${props.package.friendlyId}`}
        closable={false}
        keyboard={false}
        maskClosable={false}
        destroyOnClose={true}
        open={props.open}
        confirmLoading={isLoading}
        cancelButtonProps={{ disabled: isLoading }}
        cancelText={clone ? "Close" : "Cancel"}
        okButtonProps={{ disabled: !!clone }}
        okText={isLoading ? "Cloning..." : "Clone Package"}
        onOk={submitForm}
        onCancel={closeAndResetModal}
    >
        <Form layout="vertical"
            form={form}
            initialValues={{
                packageTypeId: props.package.packageTypeId,
                owners: mapOwners(props.package)
            }}>
            <Form.Item>
                <Alert type="info" message="All batches & cards will move to the newly cloned package and this package will become read-only." />
            </Form.Item>

            <Form.Item label="Package Type" name="packageTypeId">
                <Select
                    disabled={types?.length === 0}
                    onChange={togglePackageTypeOptions}
                    options={types?.map(t =>
                    ({
                        label: t.enabled ? t.internalName : `${t.internalName} (Inactive)`,
                        value: t.id, packageType: t
                    }))}
                />
            </Form.Item>
            <Form.Item
                name="owners"
                rules={[
                    { validator: async (_: Rule, owners: Owners) => await ownershipValidator(currentPkgType!.supportSilentAccounts, owners) }
                ]}
            >
                <OwnershipSelector
                    supportSilentAccounts={currentPkgType?.supportSilentAccounts}
                    allowMultiple={currentPkgType?.allowMultipleOwners}
                />
            </Form.Item>

            {error && <Form.Item>
                <Alert type="error" showIcon message={error} />
            </Form.Item>}

            {clone && <Form.Item>
                <Alert type="success" showIcon message={`Cloned into #${clone.friendlyId} successfully!`} />
            </Form.Item>}
        </Form>
    </Modal >;
};

export default ClonePackageModal;